import type {
  ParsedCSVRow,
  ValidationResult,
} from '@/lib/types/bulk-import.types';
import { isKnownRoleKey } from './roles.utils';

function parseCSVLine(line: string): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"' && inQuotes && nextChar === '"') {
      current += '"';
      i++;
      continue;
    }

    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }

    if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current.trim());
  return values;
}

export function parseGroupImportKeys(value?: string): string[] {
  if (!value) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .split(',')
        .map((key) => key.trim().toLowerCase())
        .filter((key) => key.length > 0)
    )
  );
}

/**
 * Parse a CSV file into an array of row objects
 */
export async function parseCSV(file: File): Promise<ParsedCSVRow[]> {
  const text = await file.text();
  const lines = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .filter((line) => line.trim());

  if (lines.length === 0) {
    throw new Error('CSV file is empty');
  }

  // Parse header row
  const headers = parseCSVLine(lines[0]).map((h) =>
    h.trim().toLowerCase().replace(/"/g, '')
  );

  // Validate headers
  if (!headers.includes('email')) {
    throw new Error('CSV must contain an "email" column');
  }

  const emailIndex = headers.indexOf('email');
  const fullNameIndex = headers.includes('full_name')
    ? headers.indexOf('full_name')
    : headers.includes('full name')
      ? headers.indexOf('full name')
      : -1;
  const roleIndex = headers.includes('role') ? headers.indexOf('role') : -1;
  const groupImportKeysIndex = headers.includes('group_import_keys')
    ? headers.indexOf('group_import_keys')
    : headers.includes('groups')
      ? headers.indexOf('groups')
      : -1;

  // Parse data rows
  const rows: ParsedCSVRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);

    if (values.length === 0 || values.every((v) => !v)) {
      continue; // Skip empty rows
    }

    const email = values[emailIndex] || '';
    const full_name =
      fullNameIndex >= 0 ? values[fullNameIndex] || undefined : undefined;
    const roleValue =
      roleIndex >= 0 ? values[roleIndex]?.trim().toLowerCase() : undefined;
    const role = roleValue || undefined;
    const group_import_keys =
      groupImportKeysIndex >= 0
        ? values[groupImportKeysIndex] || undefined
        : undefined;

    const row: ParsedCSVRow = {
      email,
      full_name,
      role,
      group_import_keys,
      groupImportKeys: parseGroupImportKeys(group_import_keys),
      _rowNumber: i + 1, // 1-indexed for user display
      _errors: [],
    };

    // Validate the row
    const validation = validateUserRow(row);
    row._errors = validation.errors;

    rows.push(row);
  }

  return rows;
}

/**
 * Validate a single CSV row
 */
export function validateUserRow(row: ParsedCSVRow): ValidationResult {
  const errors: string[] = [];

  // Email validation
  if (!row.email || row.email.trim() === '') {
    errors.push('Email is required');
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(row.email)) {
      errors.push('Invalid email format');
    }
  }

  // Role validation (if provided). The DB foreign key
  // (user_profiles_role_fkey -> roles(key)) is the source of truth; here we
  // check against the admin-managed role registry so newly created roles are
  // accepted instead of a stale hardcoded list.
  if (row.role && row.role.trim() !== '') {
    const key = row.role.trim().toLowerCase();
    if (!isKnownRoleKey(key)) {
      errors.push(`Invalid role "${row.role}".`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Generate a CSV template with headers and example rows
 */
export function generateTemplateCSV(): string {
  return `email,full_name,role,group_import_keys
john@example.com,John Doe,fellow,"fellow_2026,y1_fellow"
jane@example.com,Jane Smith,fellow_alumni,fellow_alumni
bob@example.com,Bob Jones,circle_alumni,`;
}
